import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { DynamicForm, FormFieldDefinition } from '@repo/forms';
import { useCallback } from 'react';
import { z } from 'zod';

// Import the zod schemas directly
export const usStates = z.enum([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE',
  'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
  'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
  'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
]);

export const usRegions = z.enum([
  'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West', 'All'
]);


// State name mapping
const stateNames: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

// Type definitions based on the schemas
type UsState = z.infer<typeof usStates>;
type UsRegion = z.infer<typeof usRegions>;
type Preferences = {
  party: number;
  sports: number;
  diversity: number;
  religious: number;
  industryFocused: number;
  academicFocus: number;
  socialLife: number;
  urban: number;
  prestige: number;
};

// Form values type
type FormValues = {
  region: UsRegion[];
  state: UsState[];
  tuitionMatters: boolean;
  party: number;
  sports: number;
  diversity: number;
  religious: number;
  industryFocused: number;
  academicFocus: number;
  socialLife: number;
  urban: number;
  prestige: number;
};

interface UserUniversityPreferenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (preferences: {
    region: UsRegion[];
    state: UsState[];
    tuitionMatters: number;
    preferences: Preferences;
  }) => void;
  initialValues?: {
    region?: UsRegion[];
    state?: UsState[];
    tuitionMatters?: number;
    preferences?: Preferences;
  };
}

const UserUniversityPreferenceDialog = ({
  open,
  onClose,
  onSave,
  initialValues
}: UserUniversityPreferenceDialogProps) => {
  const regions: UsRegion[] = usRegions.options;
  const states: UsState[] = usStates.options;

  const formFields: FormFieldDefinition[] = [
    {
      name: 'region',
      label: 'Preferred Regions',
      type: 'select',
      options: regions.map(region => ({ value: region, label: region })),
      multiple: true,
      helperText: 'Optional - you may select specific states if you prefer.',
      defaultValue: []
    },
    {
      name: 'state',
      label: 'Preferred States',
      type: 'select',
      options: states.map(state => ({ value: state, label: stateNames[state] || state })),
      multiple: true,
      helperText: 'Optional - you may select entire regions instead. You can also choose multiple states.',
      defaultValue: []
    },
    {
      name: 'tuitionMatters',
      label: 'Does tuition cost matter to you?',
      type: 'checkbox',
      defaultValue: false
    },
    {
      type: 'header',
      label: 'University Culture Preferences'
    },
    {
      name: 'party',
      label: 'Party Scene Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'sports',
      label: 'Sports Culture Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'diversity',
      label: 'Diversity Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'religious',
      label: 'Religious Environment Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'academicFocus',
      label: 'Academic Focus Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'industryFocused',
      label: 'Industry-Focused/Co-op Programs Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'socialLife',
      label: 'Social Life Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'urban',
      label: 'Urban Environment Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    },
    {
      name: 'prestige',
      label: 'Name Brand/Prestige Importance',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      leftLabel: '😤 Strongly Disagree',
      middleLabel: '😶 No Preference',
      rightLabel: '🤩 Strongly Agree',
      defaultValue: 5
    }
  ];

  const handleSubmit = useCallback((values: FormValues) => {
    // Convert checkbox value to number for tuitionMatters
    const tuitionMatters = values.tuitionMatters ? 10 : 0;

    // Create the preferences object
    const preferences = {
      party: values.party,
      sports: values.sports,
      diversity: values.diversity,
      religious: values.religious,
      industryFocused: values.industryFocused,
      academicFocus: values.academicFocus,
      socialLife: values.socialLife,
      urban: values.urban,
      prestige: values.prestige,
    };

    onSave({
      region: values.region || [],
      state: values.state || [],
      tuitionMatters,
      preferences
    });
    onClose();
  }, [onSave, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Set Your University Preferences</DialogTitle>
      <DialogContent>
        <Box paddingY={2}>
          <DynamicForm
            fields={formFields}
            onSubmit={handleSubmit}
            showActions={true}
            submitLabel="Save Preferences"
            previousLabel="Cancel"
            onPrevious={onClose}
            initialValues={initialValues ? {
              region: initialValues.region || [],
              state: initialValues.state || [],
              tuitionMatters: initialValues.tuitionMatters ? initialValues.tuitionMatters > 0 : false,
              party: initialValues.preferences?.party || 5,
              sports: initialValues.preferences?.sports || 5,
              diversity: initialValues.preferences?.diversity || 5,
              religious: initialValues.preferences?.religious || 5,
              industryFocused: initialValues.preferences?.industryFocused || 5,
              academicFocus: initialValues.preferences?.academicFocus || 5,
              socialLife: initialValues.preferences?.socialLife || 5,
              urban: initialValues.preferences?.urban || 5,
              prestige: initialValues.preferences?.prestige || 5,
            } : undefined}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UserUniversityPreferenceDialog; 